import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {BicDropdownElementComponent, BicSelectComponent} from '@buildinginformationcloud/bic-ui-components';
import {JsonSchema, SelectOption} from '../schema-form.types';
import {enumLabel} from '../widget-registry';
import {SchemaSourceService} from '../schema-source.service';

/** string+enum or string+x-source → dropdown (bic-select + bic-dropdown-element). */
@Component({
  selector: 'sf-select',
  standalone: true,
  imports: [BicSelectComponent, BicDropdownElementComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <bic-select
      [value]="value"
      [placeholder]="placeholder"
      [disabled]="disabled"
      (valueChange)="valueChange.emit($event)">
      @for (opt of options; track opt.value) {
        <bic-dropdown-element [value]="opt.value">{{ opt.label }}</bic-dropdown-element>
      }
    </bic-select>
  `,
})
export class SfSelectComponent implements OnInit {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  /** Read-only, z.B. weil die Zeile vom Modul verwaltet wird (siehe `x-managed-items`). */
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<any>();

  options: SelectOption[] = [];
  private readonly sources = inject(SchemaSourceService);

  /**
   * bic-select trifft die `null`-Option von `x-empty-label` nicht (es vergleicht `value ?? pendingValue`)
   * und zeigt dann den Platzhalter – der ist in diesem Fall das Leer-Label, sonst stünde dort
   * „Bitte wählen…", obwohl „nichts gewählt" eine eigene Bedeutung hat.
   */
  get placeholder(): string {
    return this.schema?.['x-empty-label'] ?? this.schema?.['x-placeholder'] ?? 'Bitte wählen…';
  }

  ngOnInit(): void {
    const source = this.schema?.['x-source'];
    if (source) {
      this.sources.getOptions(source).subscribe(opts => (this.options = opts));
    } else {
      this.options = enumOptions(this.schema, this.value);
    }
  }
}

/**
 * Optionen eines Enums für die Auswahl.
 *
 * - `x-enum-legacy`: diese Werte nur anbieten, wenn der Eintrag ihn schon hat – eine bestehende
 *   Altkarte bleibt so lesbar, neue Einträge sehen nur die aktuellen Werte.
 * - `x-empty-label`: eine erste Option „nichts gewählt" (`null`) – für Felder, deren Fehlen eine eigene
 *   Bedeutung hat (z. B. „beliebige Beziehung"). `null` statt `''`, weil der Backend-Validator einen
 *   leeren Text gegen das Enum prüfen würde.
 */
export function enumOptions(schema: JsonSchema | null | undefined, current: any): SelectOption[] {
  const legacy: any[] = schema?.['x-enum-legacy'] ?? [];
  const values = (schema?.enum ?? []).filter(v => !legacy.includes(v) || v === current);
  const options = values.map(v => ({value: v, label: enumLabel(schema, v)}));
  const emptyLabel = schema?.['x-empty-label'];
  return emptyLabel ? [{value: null, label: emptyLabel}, ...options] : options;
}
